import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listTags, createTag, addTagToDocument, removeTagFromDocument } from '../services/tagsApi'
import type { Tag } from '../services/tagsApi'

/** List tags for the given actor */
export function useTags(actorId?: string) {
  return useQuery<Tag[]>({
    queryKey: ['tags', actorId],
    queryFn: () => listTags(actorId),
    enabled: !!actorId,
    staleTime: 60_000,
  })
}

/** Create a new tag */
export function useCreateTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ name, color, actorId }: { name: string; color: string; actorId?: string }) =>
      createTag(name, color, actorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
    },
  })
}

/** Add a tag to a document */
export function useAddTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ koblinkId, tagId, actorId }: { koblinkId: string; tagId: number; actorId?: string }) =>
      addTagToDocument(koblinkId, tagId, actorId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['document', variables.koblinkId] })
    },
  })
}

/** Remove a tag from a document */
export function useRemoveTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ koblinkId, tagId, actorId }: { koblinkId: string; tagId: number; actorId?: string }) =>
      removeTagFromDocument(koblinkId, tagId, actorId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['document', variables.koblinkId] })
    },
  })
}
